import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import {
  Upload,
  FileCheck,
  AlertCircle,
  Loader2,
  Trash2,
  File as FileIcon,
  Folder
} from 'lucide-react';

function PdfUploader({
  onUploadSuccess,
  onUploadError,
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  allowedFileTypes = ['application/pdf']
}) {
  // State management
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [processingResponses, setProcessingResponses] = useState();
  const [flag, setflag] = useState(false);
console.log("pp",selectedFiles)
  // Refs and local storage
  const fileInputRef = useRef(null);
  const userEmail = localStorage.getItem("email") || '';
  const userID = localStorage.getItem("userID") || '';

  // File validation logic
  const validateFiles = (files) => {
    const validFiles = [];
    const newErrors = {};

    files.forEach((file) => {
      // Check file type
      if (!allowedFileTypes.includes(file.type)) {
        newErrors[file.name] = `Only ${allowedFileTypes.join(', ')} files are allowed`;
        return;
      }

      // Check file size
      if (file.size > maxFileSize) {
        newErrors[file.name] = `File exceeds maximum size of ${maxFileSize / 1024 / 1024}MB`;
        return;
      }

      validFiles.push(file);
    });

    return { validFiles, newErrors };
  };

  // File drop handler
  const onDrop = (acceptedFiles) => {
    if (selectedFiles.length + acceptedFiles.length > 10) {
      alert("You can only upload up to 10 files.");
      setflag(true);
      return;
    }
    const processedFiles = acceptedFiles.map(file => {
      // Create a new File object with just the filename
      const filename = file.name;
      
      // If you need to preserve the file but change how it's identified:
      // This creates a new File object but keeps the same content
      const newFile = new File(
        [file], 
        filename,  // Just the filename without the path
        { type: file.type }
      );
      
      return newFile;
    });
    const { validFiles, newErrors } = validateFiles(processedFiles);

    // Update errors
    if (Object.keys(newErrors).length > 0) {
      setErrors(prevErrors => ({ ...prevErrors, ...newErrors }));
    }

    // Add valid files (avoid duplicates)
    setSelectedFiles(prevFiles => {
      const existingNames = new Set(prevFiles.map(f => f.name));
      const newFiles = validFiles.filter(file => !existingNames.has(file.name));
      return [...prevFiles, ...newFiles];
    });
  };

  // Configure react-dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    noClick: true, // Prevent opening file dialog when clicking (we have separate buttons for that)
    noKeyboard: true, // Disable keyboard navigation
    useFsAccessApi: false // Set to true to enable folder selection in modern browsers
  });

  // File change handler for traditional file inputs
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    onDrop(files);
    
    // Clear the input after processing
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove file from selection
  const removeFile = (fileName) => {
    setSelectedFiles(prevFiles => prevFiles.filter(file => file.name !== fileName));
    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      delete newErrors[fileName];
      return newErrors;
    });
    setUploadProgress(prevProgress => {
      const newProgress = { ...prevProgress };
      delete newProgress[fileName];
      return newProgress;
    });
  };

  // Upload files to server
  const uploadFiles = async () => {
    if (selectedFiles.length === 0) {
      setErrors(prev => ({ ...prev, general: 'No files selected' }));
      return;
    }

    setIsUploading(true);
    setErrors({});

    // Initialize progress tracking
    const newProgress = {};
    selectedFiles.forEach(file => {
      newProgress[file.name] = 0;
    });
    setUploadProgress(newProgress);

    try {
      try {
        const formData = new FormData();
        formData.append("user_email_id", userEmail);
        selectedFiles.forEach(file => {
          formData.append("files", file);
        });

        const baseURL = `${process.env.REACT_APP_API_BASE_URL}/upload`;
        const response = await axios.post(baseURL, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            selectedFiles.forEach(file => {
              setUploadProgress(prev => ({ ...prev, [file.name]: percentCompleted }));
            });
          }
        });
        setProcessingResponses(response.data);
      }
      catch (error) {
        selectedFiles.forEach(file => {
          console.error(`Upload error for ${file.name}:`, error);
        });

        const errorMessage = error.response?.data?.message || error.message || 'Unknown upload error';
        setProcessingResponses(error);
        selectedFiles.forEach(file => {
          setErrors(prev => ({
            ...prev,
            [file.name]: `Failed to upload: ${errorMessage}`
          }));
        });
      }

      // Clear input and selected files
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (generalError) {
      const errorMessage = generalError.message || 'An unexpected error occurred';

      setErrors(prev => ({
        ...prev,
        general: `Upload process failed: ${errorMessage}`
      }));
    } finally {
      setIsUploading(false);
    }
  };

  // Reset upload state
  const resetUpload = () => {
    setSelectedFiles([]);
    setUploadedFiles([]);
    setErrors({});
    setUploadProgress({});
    setProcessingResponses();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  return (
    <div className="w-full mx-auto mt-[-50px]">
      {/* Error Display */}
      {errors.general && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <span className="text-sm">{errors.general}</span>
        </div>
      )}

      {/* Success Modal */}
      {processingResponses?.raw_uploaded_response?.uploaded_files?.length > 0 && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 z-50 flex items-center justify-center">
          <div className="w-full max-w-md mx-auto p-5 rounded-2xl shadow-lg bg-white relative max-h-[60vh] overflow-y-auto">
            <button
              className="border-none absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-2xl"
              onClick={resetUpload}
            >
              &times;
            </button>

            <div className="flex flex-col items-center justify-center mt-4 space-y-2">
              {processingResponses.raw_uploaded_response.uploaded_files.map((file, index) => {
                // Find corresponding processed file
                const processedFile = processingResponses.preprocess_summary_response?.preprocess_response?.processed_files.find(
                  (p) => p.raw_file_name === file.file_name
                );

                return (
                  <div key={index} className="flex flex-col items-center space-y-2 w-full">
                    <div
                      className={`relative w-12 h-12 flex items-center justify-center rounded-full ${processedFile?.file_validation ? "bg-green-100" : "bg-red-100"
                        }`}
                    >
                      {processedFile?.file_validation ? (
                        <>
                          <svg
                            className="w-10 h-10 text-green-500"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path>
                          </svg>
                          <div className="absolute inset-0 animate-ping rounded-full bg-green-200 opacity-30"></div>
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-10 h-10 text-red-500"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path>
                          </svg>
                          <div className="absolute inset-0 animate-ping rounded-full bg-red-200 opacity-30"></div>
                        </>
                      )}
                    </div>

                    <h2 className="text-lg font-bold text-gray-800 text-center">{file.file_name}</h2>
                    <p className="text-xs text-gray-500 text-center">
                      {processedFile?.status_message || file.status_message}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center mt-6">
              <button
                onClick={resetUpload}
                className="border-none px-4 py-2 text-white rounded-md text-sm font-medium flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-[#0000a0] hover:bg-blue-700 w-full max-w-xs"
              >
                Start New Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Upload Section */}
      {(
        <>
          <div className="mb-4">
            <div className="flex items-center justify-center w-full">
              <div 
                {...getRootProps()}
                className={`flex flex-col w-full h-30 border-2 border-dashed rounded-lg transition-colors ${
                  isDragActive 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <p className="mb-2 text-sm text-gray-500">
                    {isDragActive 
                      ? 'Drop PDF files or folders here' 
                      : 'Drag and drop PDF files or folders here'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">PDF files only (max 10MB)</p>
                  <div className="flex gap-4 mt-2">
                    <label className="inline-flex items-center px-4 py-2 bg-[#bb1ccc] text-white rounded-lg hover:bg-blue-600 cursor-pointer transition-colors">
                      <FileIcon className="w-4 h-4 mr-2" />
                      Choose Files
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        multiple
                        disabled={isUploading}
                        onChange={handleFileChange}
                        accept="application/pdf"
                      />
                    </label>
                    <label style={{border:"1px solid #bb1ccc"}} className="inline-flex items-center px-4 py-2 text-[#bb1ccc] hover:text-[#ffffff] rounded-lg hover:bg-[#bb1ccc] cursor-pointer transition-colors ">
                    <Folder className="w-4 h-4 mr-2" />
                      Choose Folder
                      <input
                        type="file"
                        className="hidden"
                        webkitdirectory=""
                        directory=""
                        multiple
                        disabled={isUploading}
                        onChange={handleFileChange}
                        accept="application/pdf"
                      />
                    </label>
                  </div>
                </div>
                {/* Hidden input for react-dropzone */}
                <input {...getInputProps()} />
              </div>
            </div>
          </div>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <div className="mb-4 max-h-28 overflow-y-auto">
              {selectedFiles.map((file, index) => (
                <div key={index} className="flex items-start max-h-16 justify-between p-2 bg-gray-50 rounded-lg mb-1">
                  <div className="flex-1 truncate mr-2">
                    <p className="text-sm font-normal">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  {uploadProgress[file.name] > 0 && uploadProgress[file.name] < 100 ? (
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${uploadProgress[file.name]}%` }}
                      ></div>
                    </div>
                  ) : (
                    <button
                      onClick={() => removeFile(file.name)}
                      disabled={isUploading}
                      className="text-red-500 hover:text-red-700 border-none"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  {errors[file.name] && (
                    <div className="text-xs text-red-500 ml-2">{errors[file.name]}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Upload Button */}
          <div className="flex justify-center">
            <button
              onClick={uploadFiles}
              disabled={selectedFiles.length === 0 || isUploading || selectedFiles.length > 10}
              className={`border-none px-4 py-2 rounded-md text-white font-medium flex items-center justify-center min-w-[120px] ${
                selectedFiles.length === 0 || isUploading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#0000a0] hover:bg-blue-700'
              }`}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Summary and Chat QnA. Please wait for at least 30 Min.
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default PdfUploader;